import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { isSalePageSubdomain } from "./utils/subdomain";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { SalePageProvider } from "./contexts/SalePageContext";
import { CartProvider } from "./contexts/CartContext";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleRoute from "./components/RoleRoute";
import EmailVerificationGuard from "./components/EmailVerificationGuard";
import InstructorAccessGuard from "./components/InstructorAccessGuard";
import MaintenanceGuard from "./components/MaintenanceGuard";
import SubdomainRedirect from "./components/SubdomainRedirect";
import SubdomainHandler from "./components/SubdomainHandler";
import DomainRedirect from "./components/DomainRedirect";
import SalePageGuard from "./components/SalePageGuard";
import AdminPasswordGuard from "./components/AdminPasswordGuard";
import CoursedeeAccessGuard from "./components/CoursedeeAccessGuard";
import CoursedeeRouteGuard from "./components/CoursedeeRouteGuard";
import { useErudaConsole } from "./hooks/useErudaConsole";

import AppLayout from "./layouts/AppLayout";
import StudentLayout from "./layouts/StudentLayout";
import InstructorLayout from "./layouts/InstructorLayout";

import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import EmailVerify from "./pages/auth/EmailVerify";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import ActionHandler from "./pages/auth/ActionHandler";
import LineCallback from "./pages/auth/LineCallback";
import CompleteProfile from "./pages/student/CompleteProfile";
import ProfilePage from "./pages/student/ProfilePage";
import WatchHistory from "./pages/student/WatchHistory";
import SubdomainAwareLanding from "./components/SubdomainAwareLanding";
import Pricing from "./pages/Pricing";
import FAQ from "./pages/FAQ";
import VerifySalepage from "./pages/VerifySalepage";
import VerifyTrial from "./pages/VerifyTrial";
import InstructorDashboard from "./pages/instructor/InstructorDashboard";
import InstructorHub from "./pages/instructor/InstructorHub";
import StudentReports from "./pages/instructor/StudentReports";
import StudentDetail from "./pages/instructor/StudentDetail";
import InstructorMyCourses from "./pages/instructor/MyCourses";
import Analytics from "./pages/instructor/Analytics";
import ApproveStudents from "./pages/instructor/ApproveStudents";
import DiscountManagement from "./pages/instructor/DiscountManagement";
import CreateCourse from "./pages/instructor/CreateCourse";
import EditCourse from "./pages/instructor/EditCourse";
import InstructorSettings from "./pages/instructor/InstructorSettings";
import LessonList from "./pages/instructor/LessonList";
import AddLesson from "./pages/instructor/AddLesson";
import EditLesson from "./pages/instructor/EditLesson";
import InstructorSEO from "./pages/instructor/InstructorSEO";
import InstructorPublicProfile from "./pages/instructor/InstructorPublicProfile";
import ProgressPage from "./pages/student/ProgressPage";
import Checkout from "./pages/checkout/Checkout";
import CourseCheckout from "./pages/checkout/CourseCheckout";
import CourseCheckoutQR from "./pages/checkout/CourseCheckoutQR";
import CourseCheckoutNew from "./pages/checkout/CourseCheckoutNew";
import SubscriptionCheckout from "./pages/checkout/SubscriptionCheckout";
import SubscriptionSuccess from "./pages/checkout/SubscriptionSuccess";
import LearningPlayer from "./pages/learning/LearningPlayer";
import CertificatePage from "./pages/certificate/CertificatePage";
import SubscriptionManage from "./pages/subscription/SubscriptionManage";
import RenewSubscription from "./pages/subscription/RenewSubscription";
import StorageAddon from "./pages/subscription/StorageAddon";
import SubscriptionCheckoutPage from "./pages/subscription/SubscriptionCheckout";
import UnifiedCheckout from "./pages/subscription/UnifiedCheckout";
import UnifiedCheckoutSummary from "./pages/subscription/UnifiedCheckoutSummary";
import ThemeSetup from "./pages/subscription/ThemeSetup";
import UrlAddonCheckout from "./pages/subscription/UrlAddonCheckout";
import PackageSelection from "./pages/subscription/PackageSelection";
import PackageSelectionNew from "./pages/subscription/PackageSelectionNew";
import CheckoutSummary from "./pages/subscription/CheckoutSummary";
import CheckoutSummaryNew from "./pages/subscription/CheckoutSummaryNew";
import SubscriptionCheckoutSummary from "./pages/subscription/SubscriptionCheckoutSummary";
import PaymentHistory from "./pages/payment/PaymentHistory";
import CertificateGallery from "./pages/certificates/CertificateGallery";
import CertificateDetail from "./pages/certificates/CertificateDetail";
import CertificateVerification from "./pages/certificates/CertificateVerification";
import CertificateManagement from "./pages/instructor/CertificateManagement";
import CertificateSettings from "./pages/instructor/CertificateSettings";
import StorageDashboard from "./pages/instructor/StorageDashboard";
import AddonCheckout from "./pages/checkout/AddonCheckout";
import PaymentSuccess from "./pages/checkout/PaymentSuccess";
import OmiseSubscriptionCheckout from "./pages/checkout/OmiseSubscriptionCheckout";
import SubscriptionCheckoutNew from "./pages/checkout/SubscriptionCheckoutNew";
import OmiseAddonCheckout from "./pages/checkout/OmiseAddonCheckout";
import BillingDashboard from "./pages/subscription/BillingDashboard";
import RegisterInfo from "./pages/subscription/RegisterInfo";
import RegisterSuccess from "./pages/subscription/RegisterSuccess";
import OmisePaymentSuccess from "./pages/payment/OmisePaymentSuccess";
import CourseMedia from "./pages/instructor/CourseMedia";
import UserManagement from "./pages/admin/UserManagement";
import UsersManagement from "./pages/admin/UsersManagement";
import CoursesManagement from "./pages/admin/CoursesManagement";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminSelector from "./pages/admin/AdminSelector";
import CourseSystemAdmin from "./pages/admin/CourseSystemAdmin";
import SalepageSystemAdmin from "./pages/admin/SalepageSystemAdmin";
import CoursedeeAdmin from "./pages/coursedee/CoursedeeAdmin";
import CoursededDashboard from "./pages/coursedee/CoursededDashboard";
import SystemDashboard from "./pages/admin/SystemDashboard";
import InstructorDetail from "./pages/admin/InstructorDetail";
import FeaturedPartners from "./pages/admin/FeaturedPartners";
import Support from "./pages/admin/Support";
import MaintenanceControl from "./pages/admin/MaintenanceControl";
import SubscriptionPayments from "./pages/admin/SubscriptionPayments";
import PaymentSettings from "./pages/admin/PaymentSettings";
import StorageManagement from "./pages/admin/StorageManagement";
import UnifiedPaymentApproval from "./pages/admin/UnifiedPaymentApproval";
import ContentModeration from "./pages/admin/ContentModeration";
import AdminAnalytics from "./pages/admin/Analytics";
import PaymentCheck from "./pages/admin/PaymentCheck";
import TestPaymentSubmit from "./pages/admin/TestPaymentSubmit";
import CoursePublic from "./pages/courses/CoursePublic";
import ActivityLogs from "./pages/admin/ActivityLogs";
import AdminNotifications from "./pages/admin/Notifications";
import Revenue from "./pages/admin/Revenue";
import SystemSettings from "./pages/admin/SystemSettings";
import AdminDiscountManagement from "./pages/admin/DiscountManagement";
import SEOManagement from "./pages/admin/SEOManagement";
import CustomDomainManagement from "./pages/admin/CustomDomainManagement";
import OmisePayments from "./pages/admin/OmisePayments";
import ModeratorDashboard from "./pages/moderator/ModeratorDashboard";
import TermsOfService from "./pages/legal/TermsOfService";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import Maintenance from "./pages/Maintenance";
import SalePage from './components/SalePage';
import SalePageHelp from './components/SalePageHelp';
import SalePageLanding from './pages/salepage/SalePageLanding';
import SalePagePayment from './pages/salepage/SalePagePayment';
import SalePageDashboard from './pages/salepage/SalePageDashboard';
import SalePageSlipCheckout from './pages/salepage/SalePageSlipCheckout';
import SalePageMessages from './pages/salepage/SalePageMessages';
import SalePagePayments from './pages/salepage/SalePagePayments';
import SalePagePublic from './pages/salepage/SalePagePublic';
import TestPaymentNotification from './pages/salepage/TestPaymentNotification';
import SalePagePricing from './pages/salepage/SalePagePricing';
import SalePageBillingSelect from './pages/salepage/SalePageBillingSelect';
import SalePageRenew from './pages/salepage/SalePageRenew';
import SalePageCheckoutPayment from './pages/salepage/SalePageCheckoutPayment';
import SalePageCheckoutPaymentNewUser from './pages/salepage/SalePageCheckoutPaymentNewUser';
import SalePageCheckoutNew from './pages/salepage/SalePageCheckoutNew';
import SalePageCheckoutNewUser from './pages/salepage/SalePageCheckoutNewUser';
import SalePageSettings from './pages/salepage/SalePageSettings';
import SalePagePaymentHistory from './pages/salepage/SalePagePaymentHistory';
import SalePageUrlAddonManagement from './pages/salepage/SalePageUrlAddonManagement';
import BookingAddonCheckout from './pages/salepage/BookingAddonCheckout';
import SalepagePage from './pages/instructor/SalepagePage';
import UrlAddonManagement from './pages/settings/UrlAddonManagement';
import CoursedeeAIV1 from './pages/CoursedeeAIV1';
import CoursedeeAIV2 from './pages/CoursedeeAIV2';
import CoursedeeLanding from './pages/CoursedeeLanding';
import CreatorIdentitySetup from './pages/CreatorIdentitySetup';
import ContentGenerator from './pages/ContentGenerator';
import CartPage from './pages/CartPage';
import EdumiCheckoutSuccess from './pages/EdumiCheckoutSuccess';
import SalePageCheckoutSuccess from './pages/SalePageCheckoutSuccess';

// On the dedicated Sale Page host (saledi.web.app) the salepage lives at clean root
// paths (no /salepage prefix). Legacy internal links still navigate to /salepage/* —
// strip the prefix so the visible URL stays clean.
function StripSalepagePrefix() {
  const location = useLocation();
  const stripped = location.pathname.replace(/^\/salepage/, '') || '/';
  return <Navigate to={`${stripped}${location.search}${location.hash}`} replace />;
}

export default function App() {
  useErudaConsole();

  // Sale Page lives ONLY on the dedicated host saledi.web.app (fully separated from edumi.co)
  const isSalePageHost = isSalePageSubdomain();

  return (
    <HelmetProvider>
      <AuthProvider>
        <SalePageProvider>
          <CartProvider>
            <BrowserRouter>
            <DomainRedirect />
            <SubdomainRedirect />
            <SubdomainHandler />
            <CoursedeeRouteGuard />
            <Routes>
            <Route path="/maintenance" element={<Maintenance />} />
            
            {/* Sale Page System — lives ONLY on saledi.web.app, fully separated from edumi.co */}
            {isSalePageHost && (
              <>
                <Route path="/salepage" element={<Navigate to="/" replace />} />
                <Route path="/salepage/*" element={<StripSalepagePrefix />} />
                <Route path="/pricing" element={<SalePagePricing />} />
                <Route path="/checkout" element={<Navigate to="/pricing" replace />} />
                <Route path="/billing-select" element={<SalePageBillingSelect />} />
                <Route path="/payment" element={<SalePagePayment />} />
                <Route path="/dashboard" element={
                  <SalePageGuard requireActive={false}>
                    <SalePageDashboard />
                  </SalePageGuard>
                } />
                <Route path="/messages" element={
                  <SalePageGuard requireActive={true}>
                    <SalePageMessages />
                  </SalePageGuard>
                } />
                <Route path="/payments" element={
                  <SalePageGuard requireActive={true}>
                    <SalePagePayments />
                  </SalePageGuard>
                } />
                <Route path="/settings" element={
                  <EmailVerificationGuard>
                    <SalePageSettings />
                  </EmailVerificationGuard>
                } />
                <Route path="/payment-history" element={
                  <EmailVerificationGuard>
                    <SalePagePaymentHistory />
                  </EmailVerificationGuard>
                } />
                <Route path="/url-addon" element={
                  <EmailVerificationGuard>
                    <SalePageUrlAddonManagement />
                  </EmailVerificationGuard>
                } />
                <Route path="/renew" element={
                  <EmailVerificationGuard>
                    <SalePageRenew />
                  </EmailVerificationGuard>
                } />
                <Route path="/checkout-slip" element={<SalePageSlipCheckout />} />
                <Route path="/checkout-new" element={<SalePageCheckoutNew />} />
                <Route path="/booking-addon-checkout" element={<BookingAddonCheckout />} />
                <Route path="/checkout-payment" element={<SalePageCheckoutPayment />} />
                <Route path="/checkout-payment-new-user" element={<SalePageCheckoutPaymentNewUser />} />
                <Route path="/checkout-new-user" element={<Navigate to="/checkout-payment-new-user" replace />} />
                <Route path="/test-payment" element={<TestPaymentNotification />} />
                <Route path="/checkout-success" element={<SalePageCheckoutSuccess />} />
                <Route path="/sale-page" element={
                  <SalePageGuard requireActive={false} checkPageLimit={true}>
                    <SalePage />
                  </SalePageGuard>
                } />
                <Route path="/sale-page-help" element={<SalePageHelp />} />
                <Route path="/s/:slug" element={<SalePagePublic />} />
                <Route path="/sale" element={<SalePagePublic />} />
                <Route path="/verify-salepage" element={<VerifySalepage />} />
              </>
            )}
            <Route path="/verify-trial" element={<VerifyTrial />} />
            <Route path="/identity-setup" element={<Navigate to="/v1" replace />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/edumi/checkout-success" element={<EdumiCheckoutSuccess />} />
            <Route path="/V1" element={
              <CoursedeeAccessGuard requiredVersion="v1">
                <ContentGenerator />
              </CoursedeeAccessGuard>
            } />
            <Route path="/v1" element={
              <CoursedeeAccessGuard requiredVersion="v1">
                <ContentGenerator />
              </CoursedeeAccessGuard>
            } />
            <Route path="/V2" element={
              <CoursedeeAccessGuard requiredVersion="v2">
                <CoursedeeAIV2 />
              </CoursedeeAccessGuard>
            } />
            <Route path="/v2" element={
              <CoursedeeAccessGuard requiredVersion="v2">
                <CoursedeeAIV2 />
              </CoursedeeAccessGuard>
            } />

          <Route element={<MaintenanceGuard />}>
          {/* Email Verification Page - Public but requires auth */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          
          {/* Admin Routes - MUST BE BEFORE /:username */}
          <Route
            path="/admin"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <AdminSelector />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/course-system"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <CourseSystemAdmin />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/salepage-system"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <SalepageSystemAdmin />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <AdminDashboard />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/system"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <SystemDashboard />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/instructor/:instructorId"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <InstructorDetail />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <UsersManagement />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/courses-management"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <CoursesManagement />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/subscriptions"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <UserManagement />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/user/:userId"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <UserManagement />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/partners"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <FeaturedPartners />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/support"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <Support />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/maintenance"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <MaintenanceControl />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/subscription-payments"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <SubscriptionPayments />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/payment-settings"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <PaymentSettings />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/storage"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <StorageManagement />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/payment-approval"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <UnifiedPaymentApproval />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/content-moderation"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <ContentModeration />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <AdminAnalytics />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/activity-logs"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <ActivityLogs />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <AdminNotifications />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/revenue"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <Revenue />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <SystemSettings />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/discounts"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <AdminDiscountManagement />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/seo"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <SEOManagement />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/custom-domains"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <CustomDomainManagement />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/admin/ai-system"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <CoursedeeAdmin />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />
          <Route
            path="/coursedee/dashboard"
            element={
              <EmailVerificationGuard bypassForAdmin={false}>
                <CoursededDashboard />
              </EmailVerificationGuard>
            }
          />
          <Route
            path="/admin/omise-payments"
            element={
              <AdminPasswordGuard>
                <EmailVerificationGuard bypassForAdmin={true}>
                  <OmisePayments />
                </EmailVerificationGuard>
              </AdminPasswordGuard>
            }
          />

          {/* Moderator Routes */}
          <Route
            path="/moderator"
            element={
              <RoleRoute role="moderator">
                <ModeratorDashboard />
              </RoleRoute>
            }
          />

          {/* Routes with shared AppLayout - NO RELOAD */}
          <Route element={<AppLayout />}>
            {!isSalePageHost && <Route path="/pricing" element={<Pricing />} />}
            <Route path="/faq" element={<FAQ />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/auth/action" element={<ActionHandler />} />
            <Route path="/__/auth/action" element={<ActionHandler />} />
            <Route path="/auth/verify" element={<EmailVerify />} />
            <Route path="/email-verify" element={<EmailVerify />} />
            <Route path="/auth/line/verify" element={<LineCallback />} />
            <Route path="/complete-profile" element={<CompleteProfile />} />
            <Route path="/profile" element={
              <EmailVerificationGuard>
                <ProfilePage />
              </EmailVerificationGuard>
            } />
            <Route path="/watch-history" element={
              <EmailVerificationGuard>
                <WatchHistory />
              </EmailVerificationGuard>
            } />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/instructor/dashboard" element={<InstructorDashboard />} />
            <Route path="/instructor/my-courses" element={
              <InstructorAccessGuard>
                <InstructorMyCourses />
              </InstructorAccessGuard>
            } />
            {/* Sale Page management moved to saledi.web.app — removed from edumi.co */}
            <Route path="/instructor/approve-students" element={
              <InstructorAccessGuard>
                <ApproveStudents />
              </InstructorAccessGuard>
            } />
            <Route path="/instructor/discounts" element={
              <InstructorAccessGuard>
                <DiscountManagement />
              </InstructorAccessGuard>
            } />
            <Route path="/payment/history" element={
              <EmailVerificationGuard>
                <PaymentHistory />
              </EmailVerificationGuard>
            } />
            <Route path="/subscription/manage" element={
              <EmailVerificationGuard>
                <SubscriptionManage />
              </EmailVerificationGuard>
            } />
            <Route path="/subscription/renew" element={
              <EmailVerificationGuard>
                <RenewSubscription />
              </EmailVerificationGuard>
            } />
            <Route path="/subscription/storage-addon" element={
              <EmailVerificationGuard>
                <StorageAddon />
              </EmailVerificationGuard>
            } />
            <Route path="/subscription/checkout" element={
              <EmailVerificationGuard>
                <SubscriptionCheckoutPage />
              </EmailVerificationGuard>
            } />
            <Route path="/certificates" element={<CertificateGallery />} />
            <Route path="/certificates/:certificateId" element={<CertificateDetail />} />
            <Route path="/verify" element={<CertificateVerification />} />
            <Route path="/verify/:verificationCode" element={<CertificateVerification />} />
            <Route path="/certificate/:courseId" element={<CertificatePage />} />
            <Route path="/subscription/select" element={<PackageSelection />} />
            <Route path="/subscription/checkout/summary" element={
              <EmailVerificationGuard>
                <SubscriptionCheckoutSummary />
              </EmailVerificationGuard>
            } />
            <Route path="/subscription/checkout" element={<SubscriptionCheckout />} />
            <Route path="/subscription/checkout/omise" element={<OmiseSubscriptionCheckout />} />
            <Route path="/subscription/checkout-new" element={<SubscriptionCheckoutNew />} />
            <Route path="/subscription/register-info" element={<RegisterInfo />} />
            <Route path="/subscription/register-success" element={<RegisterSuccess />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
            <Route path="/subscription/theme-setup" element={<ThemeSetup />} />
            <Route path="/subscription/billing" element={
              <ProtectedRoute>
                <BillingDashboard />
              </ProtectedRoute>
            } />
            <Route path="/payment/omise/success" element={
              <ProtectedRoute>
                <OmisePaymentSuccess />
              </ProtectedRoute>
            } />
            <Route path="/checkout/addon/omise" element={
              <ProtectedRoute>
                <OmiseAddonCheckout />
              </ProtectedRoute>
            } />
          </Route>
          
          {/* Instructor Public Profile - FREE tier (long URL) */}
          <Route path="/instructor/:instructorId" element={<InstructorPublicProfile />} />
          
          
          {/* PRO tier - Short URL with username - MUST BE LAST */}
          <Route path="/:username" element={<InstructorPublicProfile />} />

          {/* Instructor Routes with InstructorLayout */}
          <Route
            path="/instructor/hub"
            element={
              <ProtectedRoute>
                <RoleRoute allowedRoles={["instructor"]}>
                  <InstructorLayout>
                    <InstructorHub />
                  </InstructorLayout>
                </RoleRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/instructor/analytics"
            element={
              <RoleRoute role="instructor">
                <InstructorLayout>
                  <Analytics />
                </InstructorLayout>
              </RoleRoute>
            }
          />

          <Route
            path="/instructor/courses/new"
            element={
              <RoleRoute role="instructor">
                <InstructorAccessGuard>
                  <InstructorLayout>
                    <CreateCourse />
                  </InstructorLayout>
                </InstructorAccessGuard>
              </RoleRoute>
            }
          />

          <Route
            path="/instructor/courses/:id/edit"
            element={
              <RoleRoute role="instructor">
                <InstructorAccessGuard>
                  <InstructorLayout>
                    <EditCourse />
                  </InstructorLayout>
                </InstructorAccessGuard>
              </RoleRoute>
            }
          />

          <Route
            path="/instructor/courses/:id/lessons"
            element={
              <RoleRoute role="instructor">
                <InstructorAccessGuard>
                  <InstructorLayout>
                    <LessonList />
                  </InstructorLayout>
                </InstructorAccessGuard>
              </RoleRoute>
            }
          />

          <Route
            path="/instructor/courses/:id/lessons/new"
            element={
              <RoleRoute role="instructor">
                <InstructorAccessGuard>
                  <InstructorLayout>
                    <AddLesson />
                  </InstructorLayout>
                </InstructorAccessGuard>
              </RoleRoute>
            }
          />

          <Route
            path="/instructor/courses/:id/lessons/:lessonId/edit"
            element={
              <RoleRoute role="instructor">
                <InstructorAccessGuard>
                  <InstructorLayout>
                    <EditLesson />
                  </InstructorLayout>
                </InstructorAccessGuard>
              </RoleRoute>
            }
          />

          <Route
            path="/instructor/certificates"
            element={
              <RoleRoute role="instructor">
                <InstructorLayout>
                  <CertificateManagement />
                </InstructorLayout>
              </RoleRoute>
            }
          />

          <Route
            path="/instructor/certificate-settings"
            element={
              <RoleRoute role="instructor">
                <InstructorLayout>
                  <CertificateSettings />
                </InstructorLayout>
              </RoleRoute>
            }
          />

          <Route
            path="/instructor/settings"
            element={
              <RoleRoute role="instructor">
                <InstructorAccessGuard>
                  <InstructorLayout>
                    <InstructorSettings />
                  </InstructorLayout>
                </InstructorAccessGuard>
              </RoleRoute>
            }
          />

          <Route
            path="/settings/url-addon"
            element={
              <RoleRoute role="instructor">
                <EmailVerificationGuard>
                  <UrlAddonManagement />
                </EmailVerificationGuard>
              </RoleRoute>
            }
          />

          <Route
            path="/instructor/seo"
            element={
              <RoleRoute role="instructor">
                <InstructorLayout>
                  <InstructorSEO />
                </InstructorLayout>
              </RoleRoute>
            }
          />

          <Route
            path="/instructor/storage"
            element={
              <RoleRoute role="instructor">
                <InstructorAccessGuard>
                  <InstructorLayout>
                    <StorageDashboard />
                  </InstructorLayout>
                </InstructorAccessGuard>
              </RoleRoute>
            }
          />

          <Route
            path="/checkout/addon"
            element={
              <RoleRoute role="instructor">
                <InstructorLayout>
                  <AddonCheckout />
                </InstructorLayout>
              </RoleRoute>
            }
          />
          <Route
            path="/subscription/url-addon"
            element={
              <ProtectedRoute>
                <UrlAddonCheckout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-success"
            element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            }
          />

          <Route
            path="/instructor/courses/:courseId/media"
            element={
              <RoleRoute role="instructor">
                <InstructorLayout>
                  <CourseMedia />
                </InstructorLayout>
              </RoleRoute>
            }
          />

          <Route
            path="/instructor/students"
            element={
              <RoleRoute role="instructor">
                <InstructorAccessGuard>
                  <InstructorLayout>
                    <StudentReports />
                  </InstructorLayout>
                </InstructorAccessGuard>
              </RoleRoute>
            }
          />

          <Route
            path="/instructor/student/:studentId"
            element={
              <RoleRoute role="instructor">
                <InstructorLayout>
                  <StudentDetail />
                </InstructorLayout>
              </RoleRoute>
            }
          />

          {/* Student Pages */}
          <Route
            path="/courses/:id"
            element={<CoursePublic />}
          />

          <Route
            path="/courses/:id/learn/:lessonId"
            element={
              <ProtectedRoute>
                <StudentLayout>
                  <LearningPlayer />
                </StudentLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/progress"
            element={
              <ProtectedRoute>
                <ProgressPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/checkout/:courseId"
            element={
              <ProtectedRoute>
                <CourseCheckoutNew />
              </ProtectedRoute>
            }
          />

          <Route
            path="/course/:courseId/checkout"
            element={
              <ProtectedRoute>
                <CourseCheckoutQR />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/payment-check"
            element={<PaymentCheck />}
          />
          
          <Route
            path="/admin/test-payment"
            element={<TestPaymentSubmit />}
          />
          
          {/* Main Landing - EduMi courses page */}
          <Route path="/" element={<SubdomainAwareLanding />} />
          </Route>

        </Routes>
            </BrowserRouter>
          </CartProvider>
        </SalePageProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
